/**
 * Recovery from a stale build.
 *
 * Every route is a dynamic `import()` of a hashed chunk (`routes.tsx`). A
 * deploy replaces `assets/*.js` with new hashes and deletes the old files, so a
 * browser still holding the previous `index.html` — an open tab, or a cached
 * document — asks for a chunk that no longer exists and the import rejects with
 * «Failed to fetch dynamically imported module». The page is not broken; the
 * tab is simply one release behind.
 *
 * The cure is to fetch the document again, once. `location.reload()` always
 * revalidates the main document, so the browser lands on the new `index.html`
 * and with it the chunk names that exist. The flag stops that from becoming a
 * loop when the chunk is missing for some other reason — a bad upload, an
 * offline device — and in that case the route's error boundary takes over.
 */
const RELOAD_FLAG = 'clinic.staleBuildReload';

/**
 * Session storage throws outright in some privacy modes, so every access is
 * guarded: failing to remember costs us one extra reload, failing loudly costs
 * the patient the page.
 */
function readFlag(): boolean {
  try {
    return window.sessionStorage.getItem(RELOAD_FLAG) !== null;
  } catch {
    return false;
  }
}

function writeFlag(): void {
  try {
    window.sessionStorage.setItem(RELOAD_FLAG, '1');
  } catch {
    // ignored — see readFlag
  }
}

function clearFlag(): void {
  try {
    window.sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    // ignored — see readFlag
  }
}

/**
 * The browsers word this differently — Chrome «Failed to fetch dynamically
 * imported module», Firefox «error loading dynamically imported module»,
 * Safari «Importing a module script failed» — and none of them give it a code,
 * so the message is all there is to match on.
 */
export function isStaleBuildError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  return /dynamically imported module|Importing a module script failed|Failed to load module script/i.test(
    error.message,
  );
}

type ModuleLoader<T> = () => Promise<T>;

/**
 * Wraps a route's dynamic import so a release that landed mid-session recovers
 * by itself instead of showing the patient an error.
 */
export function recoverFromStaleBuild<T>(load: ModuleLoader<T>): ModuleLoader<T> {
  return async (): Promise<T> => {
    try {
      const module = await load();
      // A chunk loaded, so this tab matches the deployed build: arm the
      // recovery again for whatever deploy comes next in this session.
      clearFlag();

      return module;
    } catch (error) {
      if (!isStaleBuildError(error) || readFlag()) {
        throw error;
      }

      writeFlag();
      window.location.reload();

      // The reload is already under way; resolving or rejecting here would only
      // race it into rendering something the patient sees for a blink.
      return await new Promise<T>(() => {
        // deliberately never settles
      });
    }
  };
}
